/**
 * Shared feed read-request helpers.
 */

/** Payloads that accept an optional device timezone offset. */
export type FeedTimezonePayload = {
  timezoneOffset?: number;
};

/**
 * JS `Date.getTimezoneOffset()`: minutes that UTC is ahead of local
 * (IST → -330, EDT → 240). Feed/comment/notification *reads* use this sign
 * because the API converts with `utc.AddMinutes(-timezoneOffset)`.
 *
 * Sending minutes-east (IST → +330) made that conversion invert the offset:
 * a 7:52 PM IST post (14:22 UTC) came back as 8:52 AM.
 *
 * Create/update still send minutes-east via `getTimezoneOffsetMinutes()`.
 */
const getFeedReadTimezoneOffsetMinutes = (): number =>
  new Date().getTimezoneOffset();

/**
 * Ensures feed read requests include the current device timezone offset.
 */
export const withFeedTimezoneOffset = <T extends FeedTimezonePayload>(
  payload: T,
): T & { timezoneOffset: number } => {
  const provided = payload.timezoneOffset;
  const timezoneOffset =
    typeof provided === 'number' && Number.isFinite(provided)
      ? provided
      : getFeedReadTimezoneOffsetMinutes();

  return {
    ...payload,
    timezoneOffset,
  };
};
